import {
  deleteExpoSecureStoreItemAsync,
  getExpoSecureStoreItemAsync,
  setExpoSecureStoreItemAsync,
} from "@whoosh/utilities/src/ExpoSecureStore/expoSecureStore";

import { WHOOSH_SESSION_STORAGE_KEY } from "../constants";

export const NATIVE_SESSION_SIGNED_OUT_KEY = "whoosh-native-session-signed-out";
export const NATIVE_BIOMETRIC_SESSION_KEY =
  "whoosh-native-biometric-session-v1";

let persistenceGeneration = 0;
let isPersistenceSuspended = false;
const MEMORY_ONLY_KEY = "whoosh-native-memory-session-v1";
let memoryOnly: boolean | undefined;
let memorySession: string | null = null;
let biometricSession = false;

/** Session provenance is independent of the enrollment storage preference. */
export const isNativeBiometricSession = (): boolean => biometricSession;
export const restoreNativeSessionProvenance = async (): Promise<void> => {
  biometricSession =
    (await getExpoSecureStoreItemAsync(NATIVE_BIOMETRIC_SESSION_KEY, {
      throwOnError: true,
    })) === "true";
};
const lockListeners = new Set<() => void>();
const expiryListeners = new Set<() => void>();
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let expiresAt: number | null = null;
// The backend issues 15-minute biometric sessions. Relock slightly early.
const NATIVE_SESSION_LIFETIME_MS = 14.5 * 60 * 1000;
export const onNativeSessionExpiry = (listener: () => void): (() => void) => {
  expiryListeners.add(listener);
  return () => {
    expiryListeners.delete(listener);
  };
};
const clearExpiry = (): void => {
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  expiresAt = null;
};

export const onNativeSessionLock = (listener: () => void): (() => void) => {
  lockListeners.add(listener);
  return () => {
    lockListeners.delete(listener);
  };
};
export const isNativeSessionLocked = (): boolean => isPersistenceSuspended;
const usesMemorySession = async (): Promise<boolean> => {
  memoryOnly ??=
    (await getExpoSecureStoreItemAsync(MEMORY_ONLY_KEY, {
      throwOnError: true,
    })) === "true";
  return memoryOnly;
};
export const getNativeSessionKey = async (): Promise<string | null> => {
  const generation = captureNativeSessionPersistenceGeneration();
  if (expiresAt !== null && Date.now() >= expiresAt) return null;
  if (generation === null || (await isNativeSessionSignedOut())) return null;
  const key = (await usesMemorySession())
    ? memorySession
    : await getExpoSecureStoreItemAsync(WHOOSH_SESSION_STORAGE_KEY);
  return captureNativeSessionPersistenceGeneration() === generation
    ? key
    : null;
};
export const setNativeSessionMemoryOnly = (enabled: boolean): Promise<void> =>
  enqueueMutation(async () => {
    const generation = captureNativeSessionPersistenceGeneration();
    if (enabled) {
      if (!(await usesMemorySession()))
        memorySession = await getExpoSecureStoreItemAsync(
          WHOOSH_SESSION_STORAGE_KEY
        );
      await setExpoSecureStoreItemAsync(MEMORY_ONLY_KEY, "true", {
        throwOnError: true,
      });
      memoryOnly = true;
      await deleteExpoSecureStoreItemAsync(WHOOSH_SESSION_STORAGE_KEY, {
        throwOnError: true,
      });
    } else {
      if (memorySession)
        await setExpoSecureStoreItemAsync(
          WHOOSH_SESSION_STORAGE_KEY,
          memorySession,
          { throwOnError: true }
        );
      await deleteExpoSecureStoreItemAsync(MEMORY_ONLY_KEY, {
        throwOnError: true,
      });
      memoryOnly = false;
    }
    if (generation !== captureNativeSessionPersistenceGeneration())
      memorySession = null;
  });

let mutationQueue = Promise.resolve();

const enqueueMutation = <T>(mutation: () => Promise<T>): Promise<T> => {
  const mutationResult = mutationQueue.then(mutation);
  mutationQueue = mutationResult.then(
    () => undefined,
    () => undefined
  );

  return mutationResult;
};

export const captureNativeSessionPersistenceGeneration = (): number | null =>
  isPersistenceSuspended ? null : persistenceGeneration;

export const persistNativeSessionKeyIfCurrent = (
  sessionKey: string,
  requestGeneration: number | null
): Promise<void> => {
  return enqueueMutation(async () => {
    if (
      !isPersistenceSuspended &&
      requestGeneration !== null &&
      requestGeneration === persistenceGeneration
    ) {
      const memory = await usesMemorySession();
      if (requestGeneration !== captureNativeSessionPersistenceGeneration())
        return;
      if (memory) memorySession = sessionKey;
      else
        await setExpoSecureStoreItemAsync(
          WHOOSH_SESSION_STORAGE_KEY,
          sessionKey
        );
    }
  });
};

/** Installs an exchanged session only if logout or another login has not won. */
export const installNativeBiometricSession = (
  sessionKey: string,
  generation: number | null
): Promise<boolean> =>
  enqueueMutation(async () => {
    if (
      generation === null ||
      generation !== captureNativeSessionPersistenceGeneration()
    )
      return false;
    await setExpoSecureStoreItemAsync(WHOOSH_SESSION_STORAGE_KEY, sessionKey, {
      throwOnError: true,
    });
    await deleteExpoSecureStoreItemAsync(MEMORY_ONLY_KEY, {
      throwOnError: true,
    });
    memoryOnly = false;
    if (generation !== captureNativeSessionPersistenceGeneration())
      return false;
    memorySession = null;
    clearExpiry();
    expiresAt = Date.now() + NATIVE_SESSION_LIFETIME_MS;
    expiryTimer = setTimeout(() => {
      expiryListeners.forEach((listener) => listener());
    }, NATIVE_SESSION_LIFETIME_MS);
    await clearNativeSessionSignedOut({ biometric: true });
    return generation === captureNativeSessionPersistenceGeneration();
  });

export const invalidateAndClearNativeSessionKey = (): Promise<void> => {
  isPersistenceSuspended = true;
  clearExpiry();
  memorySession = null;
  biometricSession = false;
  lockListeners.forEach((listener) => listener());
  persistenceGeneration += 1;
  return enqueueMutation(async () => {
    await Promise.all([
      deleteExpoSecureStoreItemAsync(WHOOSH_SESSION_STORAGE_KEY),
      deleteExpoSecureStoreItemAsync(NATIVE_BIOMETRIC_SESSION_KEY, {
        throwOnError: true,
      }),
    ]);
  });
};

export const resumeNativeSessionPersistence = (): void => {
  clearExpiry();
  persistenceGeneration += 1;
  isPersistenceSuspended = false;
};

export const markNativeSessionSignedOut = async (): Promise<void> => {
  await setExpoSecureStoreItemAsync(NATIVE_SESSION_SIGNED_OUT_KEY, "true", {
    throwOnError: true,
  });
};

/** Complete a successful login; ordinary API header echoes do not change its source. */
export const clearNativeSessionSignedOut = async ({
  biometric = false,
}: { biometric?: boolean } = {}): Promise<void> => {
  biometricSession = biometric;
  if (biometric) {
    await setExpoSecureStoreItemAsync(NATIVE_BIOMETRIC_SESSION_KEY, "true", {
      throwOnError: true,
    });
  } else {
    await deleteExpoSecureStoreItemAsync(NATIVE_BIOMETRIC_SESSION_KEY, {
      throwOnError: true,
    });
  }
  await deleteExpoSecureStoreItemAsync(NATIVE_SESSION_SIGNED_OUT_KEY, {
    throwOnError: true,
  });
};

export const isNativeSessionSignedOut = async (): Promise<boolean> =>
  (await getExpoSecureStoreItemAsync(NATIVE_SESSION_SIGNED_OUT_KEY, {
    throwOnError: true,
  })) === "true";
